import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, GraduationCap, Globe2, Building2, UserCog, Briefcase, Bot, ArrowRight, FileText, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", Applied: "bg-cyan-100 text-cyan-700",
  Admitted: "bg-green-100 text-green-700", Closed: "bg-slate-100 text-slate-600",
  Lost: "bg-red-100 text-red-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [appr, setAppr] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/approvals/stats").then((r) => setAppr(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Total Leads", value: stats.leads, icon: Users, color: "#004030", to: "/admin/leads" },
        { label: "New Leads", value: stats.new_leads, icon: UserPlus, color: "#0a5540", to: "/admin/leads?status=New" },
        { label: "Admitted", value: stats.admitted, icon: GraduationCap, color: "#c9a227", to: "/admin/leads?status=Admitted" },
        { label: "Applications", value: stats.applications, icon: FileText, color: "#004030", to: "/admin/applications", badge: stats.new_submissions },
        ...(user?.kind === "super_admin"
          ? [
              { label: "Countries", value: stats.countries, icon: Globe2, color: "#013d2b", to: "/admin/countries" },
              { label: "Companies", value: stats.companies, icon: Building2, color: "#004030", to: "/admin/companies" },
              { label: "Employees", value: stats.employees, icon: UserCog, color: "#0a5540", to: "/admin/employees" },
              { label: "Agents", value: stats.agents, icon: Briefcase, color: "#013d2b", to: "/admin/agents" },
              { label: "AI Chats", value: stats.ai_chats, icon: Bot, color: "#c9a227", to: "/admin/ai-logs" },
            ]
          : [
              { label: "Employees", value: stats.employees, icon: UserCog, color: "#0a5540", to: "/admin/employees" },
              { label: "Agents", value: stats.agents, icon: Briefcase, color: "#013d2b", to: "/admin/agents" },
            ]),
      ]
    : [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-500 mt-1">Here's what's happening across your {user?.kind === "super_admin" ? "global network" : "office"}.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="relative bg-white rounded-2xl border border-slate-100 p-5 hover-lift block hover:border-[#004030] transition-colors">
            {c.badge > 0 && <span data-testid="new-submissions-badge" className="absolute top-3 right-3 bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full animate-pulse">{c.badge} new</span>}
            <div className="w-11 h-11 rounded-xl grid place-items-center text-white" style={{ backgroundColor: c.color }}>
              <c.icon size={20} />
            </div>
            <p className="font-display text-3xl font-bold text-[#012a1e] mt-3">{c.value ?? 0}</p>
            <p className="text-slate-500 text-sm">{c.label}</p>
          </Link>
        ))}
      </div>

      {appr && appr.total > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 mt-6" data-testid="approvals-dashboard-card">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h2 className="font-semibold text-[#012a1e] flex items-center gap-2"><ShieldCheck size={18} className="text-[#004030]" /> Approvals waiting <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{appr.total}</span></h2>
            <Link to="/admin/approvals" className="text-sm text-[#004030] font-semibold flex items-center gap-1 hover:gap-2 transition-all">Review <ArrowRight size={15} /></Link>
          </div>
          <div className="divide-y divide-slate-100">
            {appr.by_approver.map((a) => (
              <div key={a.name} className="flex items-center justify-between px-5 py-3" data-testid={`approver-row-${a.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-sm font-medium text-[#012a1e]">{a.name}</p>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-700">{a.count} pending</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 mt-6">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-semibold text-[#012a1e]">Recent Leads</h2>
          <Link to="/admin/leads" className="text-sm text-[#004030] font-semibold flex items-center gap-1 hover:gap-2 transition-all">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="divide-y divide-slate-100">
          {stats?.recent_leads?.length ? stats.recent_leads.map((l) => (
            <div key={l.id} className="flex items-center justify-between px-5 py-3.5">
              <div>
                <p className="font-medium text-[#012a1e]">{l.name}</p>
                <p className="text-xs text-slate-500">{l.course || "—"} · {l.country || "—"}</p>
              </div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span>
            </div>
          )) : <p className="px-5 py-8 text-center text-slate-400">No leads yet. They'll appear here as enquiries arrive.</p>}
        </div>
      </div>
    </div>
  );
}
